import { SettingsService } from '@/services/settings'
import { useUserStore } from '@/stores/user'
import type {
  Basket,
  BasketItem,
  CheckoutContext,
  InstantWinDraw,
  Plan,
  Product,
} from '@/types'
import type { TProductType } from '@/utils/helper-objects'
import { isDeposit, isInstantWinDraw, isPlan } from '@/utils/type-guard-utils'
import { getProductType } from '@/utils/utils'
import { computed, reactive } from 'vue'

export function useCheckoutBasket() {
  const userStore = useUserStore()

  const basket = reactive<Basket>({
    items: [],
    totalQuantity: 0,
    totalPrice: 0,
    get currency() {
      return SettingsService.activeCurrency
    },
  })

  const checkoutContext = reactive<CheckoutContext>({
    basket,
    reservationData: null,
    userCanUseCredit: false,
    availableCredit: 0,
  })

  // Computed properties
  const isEmpty = computed(() => basket.items.length === 0)
  const hasMultipleItems = computed(() => basket.items.length > 1)

  // Helper functions
  function generateItemId(
    product: Product,
    metadata?: Record<string, any>
  ): string {
    const baseId = (
      'code' in product
        ? product.code
        : 'id' in product
          ? product.id
          : 'unknown'
    ) as string

    const metadataString = metadata ? JSON.stringify(metadata) : ''

    return `${baseId}-${btoa(metadataString).replace(/[^a-zA-Z0-9]/g, '')}`
  }

  function calculateUnitPrice(product: Product): number {
    if (isPlan(product)) {
      return product.has_free_trial !== true ? product.discount_cost * 100 : 0
    } else if (isInstantWinDraw(product)) {
      return product.ticket_cost * 100
    } else if (isDeposit(product)) {
      return product.amount
    }
    return 0
  }

  function updateTotals() {
    basket.totalQuantity = basket.items.reduce(
      (sum, item) => sum + item.quantity,
      0
    )
    basket.totalPrice = basket.items.reduce(
      (sum, item) => sum + item.totalPrice,
      0
    )
  }

  // Public methods
  function addItem(
    product: Product,
    quantity: number = 1,
    metadata?: Record<string, any>
  ): BasketItem {
    const unitPrice = calculateUnitPrice(product)
    const itemId = generateItemId(product, metadata)

    // Check if item already exists
    const existingItem = basket.items.find((item) => item.id === itemId)

    if (existingItem) {
      existingItem.quantity += quantity
      existingItem.totalPrice = existingItem.unitPrice * existingItem.quantity
    } else {
      const newItem: BasketItem = {
        id: itemId,
        type: getProductType(product),
        product,
        quantity,
        unitPrice,
        totalPrice: unitPrice * quantity,
        metadata,
      }
      basket.items.push(newItem)
    }

    updateTotals()
    return existingItem || basket.items[basket.items.length - 1]
  }

  function removeItem(itemId: string): boolean {
    const index = basket.items.findIndex((item) => item.id === itemId)
    if (index !== -1) {
      basket.items.splice(index, 1)
      updateTotals()
      return true
    }
    return false
  }

  function updateItemQuantity(itemId: string, quantity: number): boolean {
    const item = basket.items.find((item) => item.id === itemId)
    if (item && quantity > 0) {
      item.quantity = quantity
      item.totalPrice = item.unitPrice * quantity
      updateTotals()
      return true
    } else if (item && quantity <= 0) {
      return removeItem(itemId)
    }
    return false
  }

  function clearBasket(): void {
    basket.items = []
    updateTotals()
  }

  function getItemsByType(type: TProductType): BasketItem[] {
    return basket.items.filter((item) => item.type === type)
  }

  function setReservationData(data: any): void {
    checkoutContext.reservationData = data
  }

  function setCreditInfo(canUseCredit: boolean, availableCredit: number): void {
    checkoutContext.userCanUseCredit = canUseCredit
    checkoutContext.availableCredit = availableCredit
  }

  // Refresh user credit balance before checkout
  async function refreshCreditBalance(): Promise<void> {
    if (!userStore.isLoggedIn) {
      return
    }

    try {
      await userStore.fetchCreditBalance()

      // Update checkout context with latest credit info
      if (userStore.user?.credit?.totals?.overall) {
        setCreditInfo(true, userStore.user.credit.totals.overall)
      }
    } catch (error) {
      console.warn('Failed to refresh credit balance:', error)
      // Don't throw - allow checkout to continue even if credit refresh fails
    }
  }

  // Create a single subscription item (for backward compatibility)
  function createSubscriptionBasket(
    plan: Plan,
    metadata?: Record<string, any>
  ): void {
    clearBasket()
    addItem(plan, 1, metadata)
  }

  // Create a single instant win item (for backward compatibility)
  function createInstantWinBasket(
    instantWin: InstantWinDraw,
    quantity: number,
    reservationData?: any
  ): void {
    clearBasket()
    addItem(instantWin, quantity, { reservationData })
    if (reservationData) {
      setReservationData(reservationData)
    }
  }

  return {
    // State
    basket,
    checkoutContext,

    // Computed
    isEmpty,
    hasMultipleItems,

    // Methods
    addItem,
    removeItem,
    updateItemQuantity,
    clearBasket,
    getItemsByType,
    setReservationData,
    setCreditInfo,
    refreshCreditBalance,
    createSubscriptionBasket,
    createInstantWinBasket,
  }
}
